import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, imgSrc, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity, BadgeDollarSign, Check, Cpu, Download, Fingerprint, Loader2, Lock, Megaphone, X,
} from "lucide-react";

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function TwinActivity() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [twins, setTwins] = useState([]);
  const [activity, setActivity] = useState({ items: [], groups: [] });
  const [upcharges, setUpcharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    try {
      const [twinRes, actRes, upRes] = await Promise.all([
        api.get("/twin/mine"),
        api.get("/twin/activity"),
        api.get("/upcharge"),
      ]);
      setTwins(twinRes.data);
      setActivity(actRes.data);
      setUpcharges(upRes.data);
    } catch (e) {
      if (!silent) toast.error(errMsg(e, "Could not load twin activity"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(() => load(true), 15000); // near-real-time feed
    return () => clearInterval(t);
  }, [user, load]);

  const respond = async (contract, action) => {
    setRespondingId(contract.id);
    try {
      await api.post(`/upcharge/${contract.id}/respond`, { action });
      toast.success(action === "approve"
        ? `Approved — ${fmtMoney(contract.amount)} released to your wallet`
        : `Declined — ${contract.client_company_name} was refunded in full`);
      load(true);
    } catch (e) {
      toast.error(errMsg(e, "Could not respond"));
    } finally {
      setRespondingId(null);
    }
  };

  if (!authLoading && (!user || !["model", "agency"].includes(user.role))) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">Models & agencies only</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate(user ? "/dashboard" : "/auth")}>{user ? "Dashboard" : "Sign in"}</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container max-w-5xl space-y-6 py-10">
        <Skeleton className="h-10 w-80" />
        <div className="grid gap-4 sm:grid-cols-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const pendingUpcharges = upcharges.filter((u) => u.status === "pending");
  const pastUpcharges = upcharges.filter((u) => u.status !== "pending");

  return (
    <div className="page-container max-w-5xl py-8 sm:py-12">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Digital twin control room</p>
      <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Your twins & their activity</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Watch how brands use your likeness in real time. Your AI profile is encrypted and locked to LOOKBOOK —
        brands only ever receive flat, watermarked images.
      </p>

      {/* PENDING UPCHARGE APPROVALS */}
      {pendingUpcharges.length > 0 && (
        <div data-testid="twin-pending-upcharges" className="mt-6 space-y-3">
          {pendingUpcharges.map((u) => (
            <Card key={u.id} className="rounded-2xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)]">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-start gap-3 text-sm">
                  <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(43,60%,30%)]" />
                  <div>
                    <p className="font-semibold text-[hsl(43,60%,22%)]">
                      {u.client_company_name} wants a {u.tier_label} upgrade for {u.model_name}
                    </p>
                    <p className="text-[hsl(43,50%,28%)]">
                      +{u.pct}% of the licence fee = <span className="font-semibold">{fmtMoney(u.amount)}</span>, already
                      escrowed from their wallet. Approve to release it to you, or decline for an instant refund to them.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="upcharge-approve-button" size="sm" disabled={respondingId === u.id}
                    onClick={() => respond(u, "approve")} className="rounded-xl">
                    {respondingId === u.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve
                  </Button>
                  <Button data-testid="upcharge-decline-button" size="sm" variant="outline" disabled={respondingId === u.id}
                    onClick={() => respond(u, "decline")} className="rounded-xl border bg-card text-destructive hover:bg-muted">
                    <X className="mr-1 h-3.5 w-3.5" /> Decline
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* TWIN CARDS */}
      <div data-testid="twin-cards" className="mt-6 grid gap-4 sm:grid-cols-2">
        {twins.map((t) => (
          <Card key={t.model_profile_id} data-testid="twin-card" className="rounded-2xl">
            <CardContent className="flex gap-4 p-4">
              <div className="h-28 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                {imgSrc(t.model_image) && <img src={imgSrc(t.model_image)} alt={t.model_name} className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-lg leading-tight">{t.model_name}</h3>
                  {t.twin ? (
                    <Badge className="rounded-full bg-[hsl(174,38%,34%)] text-[10px] hover:bg-[hsl(174,38%,34%)]">Twin trained</Badge>
                  ) : (
                    <Badge variant="secondary" className="rounded-full text-[10px]">No twin yet</Badge>
                  )}
                </div>
                {t.twin ? (
                  <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                    <p className="flex items-center gap-1.5"><Cpu className="h-3 w-3" /> Instant FaceID LoRA · v{t.twin.version} · {t.twin.ref_photo_count} reference photos</p>
                    <p className="flex items-center gap-1.5"><Lock className="h-3 w-3" /> {t.twin.encryption.algorithm} · platform-locked · not exportable</p>
                    <p className="flex items-center gap-1.5"><Fingerprint className="h-3 w-3" /> <span className="font-mono-plex">{t.twin.encryption.key_fingerprint.slice(0, 16)}…</span></p>
                    <p className="flex items-center gap-1.5"><Activity className="h-3 w-3" /> {t.stats.total_generations} generations · {t.stats.exported_finals} exported finals</p>
                  </div>
                ) : (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    The twin auto-trains the moment onboarding (ID scan + photo dataset + metrics) completes.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {twins.length === 0 && (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground sm:col-span-2">
            No model profiles yet. <Button variant="link" className="h-auto p-0 text-sm" onClick={() => navigate("/models/new")}>Create one</Button>
          </div>
        )}
      </div>

      {/* ACTIVITY FEED */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">Live activity feed</h2>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[hsl(174,38%,34%)]" /> auto-refreshes every 15s
          </span>
        </div>
        {activity.groups.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
            No twin activity yet. When a brand generates imagery with your likeness, it appears here instantly.
          </div>
        ) : (
          <div data-testid="twin-activity-feed" className="mt-4 space-y-3">
            {activity.groups.map((grp) => (
              <Card key={`${grp.client_company_name}|${grp.garment_name}|${grp.model_name}`} data-testid="twin-activity-group" className="rounded-2xl">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(174,38%,92%)] text-[hsl(174,45%,20%)]">
                      <Activity className="h-4 w-4" />
                    </span>
                    <div className="text-sm">
                      <p>
                        <span className="font-semibold">{grp.client_company_name}</span> generated{" "}
                        <span className="font-semibold">{grp.count} image{grp.count === 1 ? "" : "s"}</span> of{" "}
                        <span className="font-semibold">'{grp.garment_name}'</span> using {grp.model_name}'s likeness
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {grp.exported} exported final{grp.exported === 1 ? "" : "s"} · last {timeAgo(grp.last_at)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="gap-1 rounded-full font-mono-plex text-xs">
                    <BadgeDollarSign className="h-3 w-3" /> earned {fmtMoney(grp.earned)}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {activity.items.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent generations (transparency view)</h3>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {activity.items.slice(0, 12).map((g) => (
                <div key={g.id} data-testid="twin-generation-thumb" className="group relative overflow-hidden rounded-xl border">
                  <img src={imgSrc({ path: g.image_path })} alt={g.garment_name} className="aspect-[3/4] w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-1.5 pt-6 text-[9px] leading-tight text-white">
                    {g.garment_name}<br />{g.client_company_name} · {timeAgo(g.created_at)}
                    {g.exported && <Download className="ml-1 inline h-2.5 w-2.5" />}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground">
              You always see every image generated with your likeness — full transparency is part of your contract.
            </p>
          </div>
        )}
      </div>

      {/* UPCHARGE HISTORY */}
      {pastUpcharges.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl">Tier upgrade history</h2>
          <div className="mt-3 space-y-2">
            {pastUpcharges.map((u) => (
              <div key={u.id} data-testid="upcharge-history-item" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card p-3 text-sm">
                <span>{u.client_company_name} · {u.tier_label} · {u.model_name}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-plex text-xs">{fmtMoney(u.amount)}</span>
                  <Badge variant="secondary" className={`rounded-full text-[10px] ${u.status === "approved" ? "bg-[hsl(174,38%,92%)] text-[hsl(174,45%,20%)]" : ""}`}>
                    {u.status}
                  </Badge>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
